import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { SelfcheckExerciseAction, SelfcheckExerciseImageResource } from '@textbook-api-client';
import { BehaviorSubject, combineLatest, distinctUntilChanged, forkJoin, map, Observable } from 'rxjs';
import {
  TextbookActionsHandler,
  TextbookActionsHandlerToken
} from '../../../external/providers/textbook-actions-handler';
import { GroupInfo } from '../../models';
import { TextbookActionBase } from '../@base/textbook-action-base.model';
import { ImageScalingDirectionEnum } from "../../models/image-scaling-direction.enum";
import { first } from "rxjs/operators";
import { MAX_PERCENT } from "../../models/const/utility";
import { ActionDrawerApi } from "../@base/models/action-drawer-api";

interface ExerciseSubItem {
  isCorrect: boolean;
  answer: SelfcheckExerciseImageResource;
}

export const ANSWER_PADDING_PERCENT = 1;
export const ANSWER_PADDING_X_PERCENT = 2 * ANSWER_PADDING_PERCENT;
export const ANSWER_BTN_WIDTH_PERCENT_OF_CONTAINER = 4.75;

type IndexOrderMap = { [key in number]: number };

@Component({
  selector: 'txtb-selfcheck-exercise',
  templateUrl: './self-check-exercise.component.html',
  styleUrls: ['./self-check-exercise.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelfCheckExerciseComponent extends TextbookActionBase<SelfcheckExerciseAction> implements OnInit, OnDestroy {
  @Input() groupInfo!: GroupInfo;
  @ViewChild('wrapper') wrapper!: ElementRef<HTMLDivElement>;

  protected _action!: SelfcheckExerciseAction;
  private readonly actionBS = new BehaviorSubject<SelfcheckExerciseAction|null>(null);

  get action(): SelfcheckExerciseAction {
    return this._action;
  }

  @Input() set action(value: SelfcheckExerciseAction) {
    this._action = value;
    this.actionBS.next(value);
  }

  modalOpened = false;
  isButtonCheckReady = false;
  isButtonCheckClick = false;
  isFixMode = false;
  imagesByResourceIdMap: { [key in SelfcheckExerciseImageResource['resourceId']]: string } = {};
  readonly checkText = 'Sprawdź';
  readonly fixText = 'Popraw';
  readonly resetText = 'Resetuj';
  readonly ImageScalingToWidth = ImageScalingDirectionEnum.SCALE_TO_WIDTH;
  readonly ANSWER_PADDING_PERCENT = ANSWER_PADDING_PERCENT

  private readonly currentExerciseIndexBS = new BehaviorSubject<number>(0);
  private readonly shuffledExerciseItemsBS = new BehaviorSubject<ExerciseSubItem[][]>([]);
  private readonly exercisesAnswerButtonsSelectionBS = new BehaviorSubject<(boolean|undefined)[][]>([]);
  private readonly userAnswersCorrectnessStateBS = new BehaviorSubject<(boolean|undefined)[]>([]);
  private actionDrawerApi?: ActionDrawerApi;

  readonly scalingEnabled$ = this.actionBS.asObservable().pipe(map((action) => !!action?.meta?.imagesScalingEnabled))
  readonly currentExerciseIndex$ = this.currentExerciseIndexBS.asObservable();
  readonly exercisesAnswerButtonsSelection$ = this.exercisesAnswerButtonsSelectionBS.asObservable();
  readonly userAnswersCorrectnessState$ = this.userAnswersCorrectnessStateBS.asObservable();
  readonly numberOfCorrectAnswers$ = this.userAnswersCorrectnessState$.pipe(map((state) => state.filter((correct) => !!correct).length));
  readonly shuffledExerciseItems$ = this.shuffledExerciseItemsBS.asObservable();
  readonly shuffledExerciseItemsLength$ = this.shuffledExerciseItems$.pipe(map((items) => items.length));
  readonly currentQuestion$ = combineLatest([this.actionBS.asObservable(), this.currentExerciseIndex$]).pipe(map(([action, index]) => action?.meta?.exerciseItems[index]?.instruction || null))
  readonly currentExerciseUserAnswers$ = combineLatest([this.currentExerciseIndex$, this.exercisesAnswerButtonsSelection$]).pipe(map(([index, exerciseUserAnswers]) => exerciseUserAnswers[index] || []))
  readonly currentExerciseSubitems$: Observable<ExerciseSubItem[]> = combineLatest([this.currentExerciseIndex$, this.shuffledExerciseItems$]).pipe(map(([index, shuffledExerciseItems]) => shuffledExerciseItems[index] || []))

  readonly rows$ = combineLatest([this.actionBS.asObservable(), this.currentExerciseIndex$]).pipe(
    map(([action, index]) => action?.meta.exerciseItems[index]?.rows || 1)
  );
  readonly answerWidthPercent$ = this.rows$.pipe(map((rows) => MAX_PERCENT / rows));
  readonly answerButtonSpacePercent$ = this.rows$.pipe(
    map((rows) => {
      const baseWidth = ANSWER_BTN_WIDTH_PERCENT_OF_CONTAINER * rows;
      const parentPaddingRescaleFactor = baseWidth * (ANSWER_PADDING_X_PERCENT / MAX_PERCENT / rows);
      return baseWidth + parentPaddingRescaleFactor;
    })
  );
  readonly indexToOrderMapping$: Observable<IndexOrderMap> = combineLatest([this.currentExerciseSubitems$, this.rows$]).pipe(map(([subitems, rows]) =>
    this.buildOrderingMap(subitems.length, rows)));

  private readonly onExerciseChangeSubscription = combineLatest([
    this.currentExerciseIndexBS.asObservable().pipe(distinctUntilChanged()),
    this.actionBS.asObservable(),
  ]).subscribe(([index, action]) => {
    this.imagesByResourceIdMap = {};
    const exerciseItem = action?.meta?.exerciseItems?.[index];
    if (!!exerciseItem) {
      const resourceIdsToLoad = exerciseItem.exerciseSubitems.map((subitem) => subitem.answer.resourceId);
      exerciseItem.instruction?.resourceId && resourceIdsToLoad.push(exerciseItem.instruction?.resourceId);
      resourceIdsToLoad.forEach((resourceId) =>
        this.textbookActionsHandler.getResourceBlobAsString(resourceId).subscribe((resource) => {
          this.imagesByResourceIdMap[resourceId] = resource;
          this.cdr.markForCheck();
        }))
    }
  })

  constructor(
    @Inject(TextbookActionsHandlerToken)
    public textbookActionsHandler: TextbookActionsHandler,
    private readonly cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnDestroy(): void {
    this.onExerciseChangeSubscription.unsubscribe();
    this.currentExerciseIndexBS.complete();
    this.shuffledExerciseItemsBS.complete();
    this.exercisesAnswerButtonsSelectionBS.complete();
    this.userAnswersCorrectnessStateBS.complete();
  }


  public ngOnInit(): void {
    const exerciseItems = this.shuffleExercise(this.action.meta.exerciseItems)
    this.shuffledExerciseItemsBS.next(exerciseItems);
    this.resetUserAnswers(exerciseItems.length);
  }

  onActionDrawerApiInitialized(service: ActionDrawerApi): void {
    this.actionDrawerApi = service;
  }

  setAnswer(index: number): void {
    const exerciseItems = this.shuffledExerciseItemsBS.getValue();
    const selectedIndex = this.currentExerciseIndexBS.getValue();
    const answersButtonSelected = [...this.exercisesAnswerButtonsSelectionBS.getValue()]
    answersButtonSelected[selectedIndex] = exerciseItems[selectedIndex]
      .map((_, i) => i === index);
    const answersCorrectnessState = [...this.userAnswersCorrectnessStateBS.getValue()]
    answersCorrectnessState[selectedIndex] = undefined;
    this.userAnswersCorrectnessStateBS.next(answersCorrectnessState);
    this.exercisesAnswerButtonsSelectionBS.next(answersButtonSelected);
    this.isButtonCheckClick = false;
    this.isButtonCheckReady = this.isCheckButtonReady(answersButtonSelected.length);
  }

  isCheckButtonReady(totalResponses: number): boolean {
    const ready = this.exercisesAnswerButtonsSelectionBS
      .getValue()
      .flat()
      .reduce(
        (acc: number, answerChecked: boolean | undefined) => acc + (answerChecked ? 1 : 0),
        0
      );

    return totalResponses === ready;
  }

  isExerciseAnswerMarked(i: number): boolean {
    return !!this.exercisesAnswerButtonsSelectionBS.getValue()?.[i]?.some((answer) => answer);
  }

  changeExercise(direction: number): void {
    this.currentExerciseIndexBS.next(direction);
    this.scrollToTop();
    this.actionDrawerApi?.resetZoom();
  }

  scrollToTop(): void {
    this.wrapper.nativeElement.scroll(0, 0);
  }

  checkAnswers(): void {
    this.modalOpened = true;
    this.isButtonCheckClick = true;
    this.isFixMode = false;
    forkJoin([this.shuffledExerciseItems$.pipe(first()), this.exercisesAnswerButtonsSelection$.pipe(first())]).pipe(map(([items, userAnswers]) => items.reduce((state, subitems, exerciseItemIndex) => {
      const indexOfCorrectSubitem = subitems.findIndex((subitem) => subitem.isCorrect);
      state[exerciseItemIndex] = userAnswers[exerciseItemIndex]?.[indexOfCorrectSubitem];
      return state;
    }, [] as (boolean|undefined)[]))).subscribe((state) => {
      this.userAnswersCorrectnessStateBS.next(state);
    });
    this.actionDrawerApi?.resetZoom();
  }

  fixAnswers(): void {
    const exerciseItems = this.shuffleExercise(this.action.meta.exerciseItems, true)
    const answersState = this.userAnswersCorrectnessStateBS.getValue();
    const answersButtonSelected = [...this.exercisesAnswerButtonsSelectionBS.getValue()];
    answersState.forEach((correct, index) => !correct && (answersButtonSelected[index] = []));
    this.exercisesAnswerButtonsSelectionBS.next(answersButtonSelected);
    this.shuffledExerciseItemsBS.next(exerciseItems);
    this.isFixMode = true;
    this.hideSummaryModal();
    this.actionDrawerApi?.resetZoom();
  }

  reset(): void {
    this.isFixMode = false;
    const exerciseItems = this.shuffleExercise(this.action.meta.exerciseItems)
    this.shuffledExerciseItemsBS.next(exerciseItems);
    this.hideSummaryModal();
    this.resetUserAnswers(exerciseItems.length);
    this.changeExercise(0);
    this.actionDrawerApi?.resetZoom();
  }

  trackByAnswerItem(index: number): number {
    return index;
  }

  private resetUserAnswers(userAnswersBufferSize: number): void {
    this.exercisesAnswerButtonsSelectionBS.next(Array(userAnswersBufferSize).fill(null));
    this.userAnswersCorrectnessStateBS.next([]);
  }

  private shuffleExercise(
    exerciseItems: SelfcheckExerciseAction['meta']['exerciseItems'],
    skipCorrectAnswers = false
  ): ExerciseSubItem[][] {
    return exerciseItems.map((item, index) => {
      const isCorrect = this.userAnswersCorrectnessStateBS.getValue()[index];
      if (skipCorrectAnswers && isCorrect) {
        const shuffledItem = this.shuffledExerciseItemsBS.getValue()[index];
        return shuffledItem;
      } else {
        return this.shuffleAnswers(item.exerciseSubitems);
      }
    });
  }

  private shuffleAnswers<T extends ExerciseSubItem>(exerciseSubItems: T[]): ExerciseSubItem[] {
    return this.shuffle(exerciseSubItems).map((e) => ({
      isCorrect: e.isCorrect,
      answer: e.answer
    }));
  }

  private shuffle<T extends ExerciseSubItem>(arr: Array<T>): Array<T> {
    return [...arr].sort(() => Math.random() - 0.5);
  }

  private hideSummaryModal() {
    this.modalOpened = false;
    this.isButtonCheckReady = false;
  }

  private buildOrderingMap(subitemsLength: number, rows: number): IndexOrderMap {
    const numberOfRowsToFill = Math.floor(subitemsLength / rows);
    const maxOrder = subitemsLength;
    let currentColumn = 0;
    let currentRow = 0;
    const mapping: IndexOrderMap = {};
    for (let index = 0; index < subitemsLength; index++) {
      if (currentRow > numberOfRowsToFill) {
        currentRow = 0;
        currentColumn++;
      }
      let order = currentRow * rows + currentColumn;
      if (order >= maxOrder) {
        currentRow = 0;
        currentColumn++;
        order = currentRow * rows + currentColumn;
      }
      currentRow++;
      mapping[index] = order;
    }
    return mapping;
  }

}
